import {
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import {
  ComplianceDocumentDefinition,
  ComplianceDocumentKind,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import {
  complianceDocumentKindLabel,
  complianceDocumentTypeLabel,
} from './translations';

export function complianceDocumentDefinitionTableColumns(
  t: TFunction,
  onEdit: (definition: ComplianceDocumentDefinition) => void,
  onArchive: (definition: ComplianceDocumentDefinition) => void,
  onRestore: (id: number) => Promise<void>,
) {
  const columnHelper = createColumnHelper<ComplianceDocumentDefinition>();

  return [
    columnHelper.accessor('name', {
      cell: (info) => (
        <Flex direction="column">
          <Text variant="configuration-strong">{info.getValue()}</Text>
          <Text
            variant={
              info.row.original.archived
                ? 'entity-directory-status-inactive'
                : 'entity-directory-status-active'
            }
          >
            {info.row.original.archived
              ? t('general.archived')
              : t('complianceDocumentDefinitions.active')}
          </Text>
        </Flex>
      ),
      header: t('general.name'),
      size: 290,
    }),
    columnHelper.accessor('description', {
      header: t('general.description'),
      size: 340,
    }),
    columnHelper.accessor('kind', {
      cell: (info) => (
        <Badge
          borderRadius={0}
          colorScheme={
            compareEnum(
              ComplianceDocumentKind,
              info.getValue(),
              ComplianceDocumentKind.Certificate,
            )
              ? 'orange'
              : 'blue'
          }
          fontSize="11px"
          fontWeight="semibold"
          px={2}
          py={1}
          textTransform="none"
          variant="subtle"
        >
          {complianceDocumentKindLabel(info.getValue(), t)}
        </Badge>
      ),
      header: t('complianceDocumentDefinitions.kindLabel'),
      size: 100,
    }),
    columnHelper.accessor('type', {
      cell: (info) => complianceDocumentTypeLabel(info.getValue(), t),
      header: t('complianceDocumentDefinitions.typeLabel'),
      size: 140,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <Flex justifyContent="end">
          <Menu isLazy={true} placement="left" variant="entityDirectory">
            <MenuButton aria-label={t('general.more')}>
              <Icons.DotsHorizontal boxSize={6} />
            </MenuButton>
            <Portal>
              <MenuList>
                {!info.row.original.archived && (
                  <>
                    <MenuItem
                      icon={<Icons.Pencil boxSize={defaultIconSize} />}
                      onClick={() => onEdit(info.row.original)}
                    >
                      {t('general.edit')}
                    </MenuItem>
                    <MenuItem
                      icon={<Icons.Archive boxSize={defaultIconSize} />}
                      onClick={() => onArchive(info.row.original)}
                    >
                      {t('general.archive')}
                    </MenuItem>
                  </>
                )}
                {info.row.original.archived && (
                  <MenuItem
                    icon={<Icons.Undo boxSize={defaultIconSize} />}
                    onClick={() => onRestore(info.row.original.id)}
                  >
                    {t('general.restore')}
                  </MenuItem>
                )}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      ),
      header: '',
      size: 56,
    }),
  ];
}
